import Button from '@liquidweb-dev/undercurrent/Button';
import Tab from '@liquidweb-dev/undercurrent/Tab';
import Tabs from '@liquidweb-dev/undercurrent/Tabs';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce';
import FullScreenError from 'components/molecules/FullScreenError';
import Actions from 'components/standard/HostingDetailsActions';
import Attributes from 'components/standard/HostingDetailsAttributes';
import BackupsAlert from 'components/standard/BackupsAlert';
import Description from 'components/standard/HostingDetailsDescription';
import PageLayout, {
	PageLayoutInsetContent,
} from 'components/standard/PageLayout';
import TabBar from 'components/standard/TabBar';
import { IntroTarget, introTargets } from 'containers/introjs';
import hostingDetailsAlsowith from 'containers/pages/servers/details/hostingDetailsAlsowith';
import useReverseDns from 'modules/dns/useReverseDns';
import useAssetDetails from 'modules/queries/asset/useDetails';
import useMonitoringBandwidthStats from 'modules/queries/monitoring/bandwidth/useStats';
import { usePollNetworkIpListPublic } from 'modules/queries/network/ip/useListPublic';
import NotFound from 'pages/error/NotFound';
import React, { useState } from 'react';
import {
	Redirect,
	Link as RouterLink,
	useLocation,
	useParams,
} from 'react-router-dom';
import { isServerTypeWithHelpDocs } from 'utility/constants/serverTypeWithHelpDoc';
import { getErrorStringHelper } from 'utility/redux/selectorHelperFunctions/apiModules';
import DrawerDocsDetails from '../DrawerDocsDetails/DrawerDocsDetails';

/**
 * A layout for hosting details pages.
 */
const HostingDetailsLayout = ({ children }: { children: React.ReactNode }) => {
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const { uniqId } = useParams<{ uniqId: string }>();
	const { pathname } = useLocation();
	const {
		data: assetDetailsData,
		isLoading: assetDetailsIsLoading,
		isError: assetDetailsIsError,
		error: assetDetailsError,
	} = useAssetDetails(
		{
			uniq_id: uniqId,
			alsowith: hostingDetailsAlsowith,
		},
		{
			refetchInterval: 30000,
		},
	);
	const serverType = assetDetailsData?.type?.toLocaleLowerCase() || '';
	const { data: publicIps } = usePollNetworkIpListPublic({
		uniq_id: uniqId,
		page_size: 9999,
		page_num: 1,
		expand_ips: 1,
	});

	const { isError: isBandwidthStatsError } = useMonitoringBandwidthStats({
		uniq_id: uniqId,
	});

	const { assignableIps, isLoading: isReverseDnsLoading } = useReverseDns({
		uniqId,
	});

	if (assetDetailsIsError && assetDetailsError.statusCode === 404) {
		return <NotFound />;
	}

	if (assetDetailsIsError) {
		return (
			<FullScreenError content={getErrorStringHelper(assetDetailsIsError)} />
		);
	}

	const {
		assetType,
		canBackupsAndImages,
		canConsole,
		capabilities,
		custom_name: customName,
		custom_description: customDescription,
		disableConsole,
		domain,
		isBeyondHosting,
		isPrivateParent,
		isVmware,
		zone,
		isBareMetal,
		displayType,
	} = assetDetailsData || {};
	const { serverStatsCloud, serverStatsLocal } = capabilities || {};
	const canServerStatsCloud = fromApiBoolean(serverStatsCloud);
	const canServerStatsLocal = fromApiBoolean(serverStatsLocal);

	if (isBeyondHosting) {
		return <Redirect to={`/servers/${uniqId}/beyond-hosting`} />;
	}

	if (isVmware) {
		return <Redirect to={`/servers/${uniqId}/vmware-vdc`} />;
	}

	if (isPrivateParent) {
		return <Redirect to={`/servers/${uniqId}/private-parent`} />;
	}

	const canMonitoring =
		canServerStatsLocal || canServerStatsCloud || !isBareMetal;

	const canNetworking =
		(zone?.id && fromApiBoolean(capabilities?.autoPrivateNetworking)) ||
		!isBandwidthStatsError ||
		capabilities?.provFirewall ||
		capabilities?.nocworxFirewall ||
		(publicIps && publicIps?.items.length > 0) ||
		Boolean(assignableIps?.length);

	const title = customName || domain || '';

	// the tabs correspond to the portion of the path after the server uniqId
	const tabsValue = pathname.split('/')[3] || 'overview';

	return (
		<PageLayout
			title={title}
			attributesSlot={<Attributes />}
			actionsSlot={<Actions />}
			alert={<BackupsAlert uniqId={uniqId} />}
			isLoading={assetDetailsIsLoading}
			description={
				customDescription && (
					<Description serverDescription={customDescription} />
				)
			}
			variant="dynamic"
		>
			<TabBar
				sx={{ marginBottom: 600 }}
				isLoading={assetDetailsIsLoading || isReverseDnsLoading}
			>
				<Tabs value={tabsValue} variant="scrollable">
					<Tab
						component={RouterLink}
						value="overview"
						to={`/servers/${uniqId}`}
						label="Overview"
					/>
					{canMonitoring && (
						<Tab
							component={RouterLink}
							value="monitoring"
							to={`/servers/${uniqId}/monitoring`}
							label="Monitoring"
						/>
					)}
					{canNetworking && (
						<Tab
							component={RouterLink}
							value="networking"
							to={`/servers/${uniqId}/networking`}
							label="Networking"
						/>
					)}
					{canBackupsAndImages && (
						<Tab
							component={RouterLink}
							value="backups"
							to={`/servers/${uniqId}/backups`}
							label={assetType === 'dedicated' ? 'Backups' : 'Backups & Images'}
						/>
					)}
					<Tab
						component={RouterLink}
						value="activity"
						to={`/servers/${uniqId}/activity`}
						label="Activity"
					/>
					{canConsole && !disableConsole && (
						<Tab
							component={RouterLink}
							value="console"
							to={`/servers/${uniqId}/console`}
							label="Console"
						/>
					)}
					<Tab
						component={RouterLink}
						value="settings"
						to={`/servers/${uniqId}/settings`}
						label={
							<IntroTarget introId={introTargets.serverInteriorSettingsTab}>
								<span>Settings</span>
							</IntroTarget>
						}
					/>
				</Tabs>
				{isServerTypeWithHelpDocs(serverType) && (
					<Button
						variant="ghost"
						onClick={() => setIsDrawerOpen(true)}
						sx={({ size, vars }) => ({
							color: vars.palette.uc.text.main,
							height: size.lg,
							paddingY: 100,
							paddingX: 150,
						})}
					>
						{`${displayType} Docs`}
					</Button>
				)}
			</TabBar>
			<PageLayoutInsetContent sx={{ flexGrow: 1 }}>
				{children}
			</PageLayoutInsetContent>
			<DrawerDocsDetails
				uniqId={uniqId}
				open={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
			/>
		</PageLayout>
	);
};

export default HostingDetailsLayout;
